import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogIn, Trash2, X, Pencil, Plus } from "lucide-react";
import { SiteEditor } from "@/components/sites/SiteEditor";
import { clip } from "@/components/drop/SiteSwitcher";
import { useSession } from "@/context/SessionContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { DropFlags } from "@/components/drop/DropFlags";
import { endsShort, formatGal, formatToEighths, productColor, siteTankName, tankLabel } from "@/lib/tank";

const ago = (iso, t) => {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return t("just now");
  if (m < 60) return t("{n}m ago", { n: m });
  if (m < 1440) return t("{n}h ago", { n: Math.floor(m / 60) });
  return t("{n}d ago", { n: Math.floor(m / 1440) });
};
const STATUS = { safe: "#34D399", caution: "#F59E0B", critical: "#F87171" };

function TankLine({ tank, i, siteId, onLoad, onRemove }) {
  const t = useT();
  return (
    <div className="tank-line" data-testid={`site-tank-${siteId}-${i}`}>
      <span className="grade-dot" style={{ background: productColor(tank.product) }} />
      <div className="card-main">
        <div className="tank-line-label">{tankLabel(tank.label) || t("Tank {n}", { n: i + 1 })}</div>
        <div className="card-meta mono">{formatGal(tank.capacity)} gal · {tank.diameter}" {t(endsShort(tank.ends))} · {t("stick")} {formatToEighths(tank.startInches)}</div>
      </div>
      <button type="button" className="load-btn" data-testid={`site-load-${siteId}-${i}`} onClick={onLoad}>{t("Load")}</button>
      {onRemove && (
        <button type="button" className="icon-btn icon-btn-sm" data-testid={`site-tank-remove-${siteId}-${i}`} onClick={onRemove} aria-label={t("Remove tank")}><X size={16} /></button>
      )}
    </div>
  );
}

function SiteCard({ site, onLoad, onDelete, onRemoveTank, onEdit }) {
  const t = useT();
  return (
    <div className="card site-card site-card-col" data-testid={`site-${site.id}`}>
      <div className="card-top site-head">
        <span className="card-name">{site.name}</span>
        <span className="card-sub">{(site.tanks.length === 1 ? t("1 tank") : t("{n} tanks", { n: site.tanks.length }))}</span>
        <button type="button" className="icon-btn" data-testid={`site-edit-${site.id}`} onClick={onEdit} aria-label={t("Edit site")}><Pencil size={16} /></button>
        <button type="button" className="icon-btn" data-testid={`site-delete-${site.id}`} onClick={onDelete} aria-label={t("Delete site")}><Trash2 size={16} /></button>
      </div>
      {site.tanks.map((tank, i) => (
        <TankLine key={i} tank={tank} i={i} siteId={site.id} onLoad={() => onLoad(tank, i)}
          onRemove={site.tanks.length > 1 ? () => onRemoveTank(i) : null} />
      ))}
    </div>
  );
}

function DropCard({ drop, onDelete }) {
  const t = useT();
  return (
    <div className="card site-card" data-testid={`drop-${drop.id}`} style={{ borderLeftColor: STATUS[drop.status] }}>
      <div className="card-main">
        <div className="card-top"><span className="grade-dot" style={{ background: productColor(drop.product) }} /><span className="card-name">{siteTankName(drop.name) || `${formatGal(drop.capacity)} gal · ${drop.diameter}"`}</span></div>
        <div className="drop-line mono">{formatToEighths(drop.startInches)} → {formatToEighths(drop.finalInches)} · +{formatGal(drop.dropGallons)} gal</div>
        <div className="card-sub">{formatGal(drop.finalGallons)} gal · {drop.finalPct.toFixed(1)}% · {ago(drop.at, t)}</div>
        <DropFlags drop={drop} t={t} />
      </div>
      <button type="button" className="icon-btn" data-testid={`drop-delete-${drop.id}`} onClick={onDelete} aria-label={t("Delete drop")}><Trash2 size={16} /></button>
    </div>
  );
}

function SignedOut() {
  const t = useT();
  const nav = useNavigate();
  return (
    <div className="empty signin-prompt" data-testid="sites-signin-prompt">
      <p>{t("Sign in to save your stations and keep a history of your drops.")}</p>
      <button type="button" className="primary-btn" data-testid="sites-signin-button" onClick={() => nav("/login?next=/sites")}>
        <LogIn size={16} />{t("Sign in")}
      </button>
    </div>
  );
}

export default function SitesPage() {
  const { patch } = useSession();
  const { user } = useAuth();
  const t = useT();
  const nav = useNavigate();
  const [sites, setSites] = useState([]);
  const [drops, setDrops] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    if (user) api.getSites().then(setSites).catch(() => {});
    api.getDrops().then(setDrops).catch(() => {});
  }, [user]);
  useEffect(() => { if (user) load(); }, [user, load]);

  const loadTank = (site, x, i) => {
    patch({ capacity: x.capacity, diameter: x.diameter, ends: x.ends, startInches: x.startInches, product: x.product, activeSiteId: site.id, activeTank: i, activeLabel: x.label, activeEpa: !!x.epaTankId, activeKey: `${x.capacity}-${x.diameter}-${x.ends}`, activeName: clip(`${site.name} · ${tankLabel(x.label) || `Tank ${i + 1}`}`, 80) });
    nav("/drop");
  };
  const del = (fn, id) => fn(id).then(load);
  const removeTank = (site, i) => api.updateSite(site.id, { name: site.name, tanks: site.tanks.filter((_, j) => j !== i) }).then(load);

  return (
    <div className="screen sites-screen" data-testid="sites-screen">
      <header className="sites-header">
        <div><div className="header-title">{t("Sites & History")}</div><div className="header-sub">{t("Saved stations and recent drops")}</div></div>
        <button type="button" className="header-btn" data-testid="sites-close" onClick={() => (window.history.state?.idx > 0 ? nav(-1) : nav("/"))} aria-label={t("Close")}><X size={20} /></button>
      </header>
      <div className="screen-body">
        {user === false ? <SignedOut /> : (
          <>
            <>
            <div className="sites-title-row">
              <div className="section-title">{t("Saved sites")}</div>
              <button type="button" className="load-btn load-btn-active" data-testid="new-site-button" onClick={() => setEditing("new")}><Plus size={14} />{t("New site")}</button>
            </div>
            {sites.length ? sites.map((x) => (
              <SiteCard key={x.id} site={x} onLoad={(tank, i) => loadTank(x, tank, i)} onEdit={() => setEditing(x)} onDelete={() => del(api.removeSite, x.id)} onRemoveTank={(i) => removeTank(x, i)} />
            )) : <div className="empty" data-testid="sites-empty"><p>{t("No saved sites yet. Tap New site to add a station with all its tanks.")}</p></div>}
            </>
            <div className="section-title section-gap">{t("Recent drops")}</div>
            {drops.length ? drops.map((d) => <DropCard key={d.id} drop={d} onDelete={() => del(api.removeDrop, d.id)} />)
              : <div className="empty" data-testid="drops-empty"><p>{t("No drops logged yet. Tap \"Log drop\" after a drop.")}</p></div>}
            {editing && <SiteEditor site={editing === "new" ? null : editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
          </>
        )}
      </div>
    </div>
  );
}
